/**
 * Hex colours, as a shop picks them for its bills.
 *
 * One implementation for both sides: the API's PDF writer parses the accent it is asked to paint,
 * and the web settings page validates what the owner types and warns when it will not read as a
 * heading. Only the `#rrggbb` form is accepted — no shorthand, no names — because that is what a
 * colour input produces and what the PDF writer can turn into channels without guessing.
 */

/** A colour's three channels, each 0..255. */
export interface RgbColour {
  r: number;
  g: number;
  b: number;
}

const HEX_COLOUR = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;

/** True for a `#rrggbb` string (either case). For forms that want a yes/no rather than a throw. */
export function isHexColour(value: string): boolean {
  return HEX_COLOUR.test(value);
}

/** Split a `#rrggbb` string into its channels. Throws on anything else — never guesses black. */
export function parseHexColour(value: string): RgbColour {
  const match = HEX_COLOUR.exec(value);
  if (!match) throw new Error(`Not a #rrggbb colour: ${JSON.stringify(value)}`);
  return {
    r: Number.parseInt(match[1] ?? '', 16),
    g: Number.parseInt(match[2] ?? '', 16),
    b: Number.parseInt(match[3] ?? '', 16),
  };
}

/** WCAG 2 relative luminance: 0 for black, 1 for white. */
export function relativeLuminance(hex: string): number {
  const { r, g, b } = parseHexColour(hex);
  const linear = (channel: number): number => {
    const srgb = channel / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

/** WCAG contrast ratio between two colours, 1 (none) to 21 (black on white). */
export function contrastRatio(first: string, second: string): number {
  const a = relativeLuminance(first);
  const b = relativeLuminance(second);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** White or black — whichever reads better on a band filled with `hex`. */
export function readableTextOn(hex: string): '#ffffff' | '#000000' {
  return contrastRatio(hex, '#ffffff') >= contrastRatio(hex, '#000000') ? '#ffffff' : '#000000';
}

/**
 * True when `hex` set as heading text on white paper falls under 3:1 — the WCAG floor for large
 * text. The settings page warns rather than refuses; templates use it to pick a darker heading.
 */
export function isTooLightForHeadings(hex: string): boolean {
  return contrastRatio(hex, '#ffffff') < 3;
}
